/**
 * Settings → Roles & Permissions — per-clinic role templates.
 * Module × role matrix with Full / View only / Off levels. Owner is locked
 * to full access. Saved per clinic and enforced on the server.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Save, RotateCcw, Check, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVEL_LABEL = { full: 'Full', view: 'View only', off: 'Off' };
const LEVEL_CLS = {
  full: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  view: 'bg-amber-50 border-amber-300 text-amber-800',
  off: 'bg-slate-100 border-slate-300 text-slate-500',
};

export default function RolesPermissionsTab() {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const apply = (d) => { setData(d); setMatrix(structuredClone(d.matrix)); };
  useEffect(() => {
    axios.get(`${API}/role-permissions/matrix`).then((r) => apply(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load roles'));
  }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };
  const save = async () => {
    setBusy(true); setErr('');
    try { apply((await axios.put(`${API}/role-permissions/matrix`, { matrix })).data); flash('Permissions saved'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true); setErr('');
    try { apply((await axios.post(`${API}/role-permissions/matrix/reset`)).data); flash('Reset to AudiNexa defaults'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Reset failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-400">{err || 'Loading…'}</div>;
  const dirty = JSON.stringify(matrix) !== JSON.stringify(data.matrix);

  return (
    <div className="p-6 max-w-6xl" data-testid="settings-roles-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-1.5"><ShieldCheck size={16} /> Roles &amp; Permissions</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Choose what each role can open or change. Seats aren't tied to roles — assign any role to any staff member.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={busy} data-testid="roles-reset-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50">
            <RotateCcw size={12} /> Reset to defaults
          </button>
          <button onClick={save} disabled={busy || !dirty} data-testid="roles-save-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50">
            <Save size={12} /> {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      {msg && <div className="mb-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="roles-flash"><Check size={13} />{msg}</div>}
      {err && <div className="mb-3 flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="roles-err"><AlertTriangle size={13} />{err}</div>}
      <div className="bg-white rounded border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left min-w-[200px]">Module</th>
              {data.roles.map((r) => <th key={r.role} className="px-2 py-2 text-center" data-testid={`roles-col-${r.role}`}>{r.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.modules.map((m) => (
              <tr key={m.code} className="border-t border-slate-100">
                <td className="px-3 py-2 font-semibold text-slate-700">{m.label}</td>
                {data.roles.map((r) => (
                  <td key={r.role} className="px-1.5 py-1.5 text-center">
                    <LevelSelect
                      value={matrix[r.role]?.[m.code]}
                      levels={data.levels}
                      disabled={!r.editable}
                      testid={`perm-${r.role}-${m.code}`}
                      onChange={(v) => setMatrix((x) => ({ ...x, [r.role]: { ...x[r.role], [m.code]: v } }))}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10.5px] text-slate-500 mt-2">"View only" lets the role open a module but not save changes. Owner always has full access.</p>
    </div>
  );
}

function LevelSelect({ value, levels, disabled, onChange, testid }) {
  return (
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={`w-[92px] px-1.5 py-1 text-[11px] font-semibold border rounded ${LEVEL_CLS[value] || ''} disabled:opacity-60 disabled:cursor-not-allowed`}>
      {levels.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
    </select>
  );
}
